import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Field,
  Label,
  Switch,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  MegaphoneIcon,
  PlusIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import GeneratingPhotoSkeleton from "./GeneratingPhotoSkeleton";
import PublishedListings from "./PublishedListings";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";
import { PrimaryButtonSmall, SecondaryButtonSmall } from "../../components/Button";
import {
  VARIANT_ID,
  eventbriteListingUrl,
  hasVariantOverride,
  meetupListingUrl,
} from "../utils/listingVariants";

// A second listing creative for one event. It publishes as its own Meetup draft
// and its own Eventbrite event, both linking back to this same event, and the
// event detail page renders whichever one the visitor arrived from.
//
// The three photo actions mirror PhotosInput, with one difference that runs all
// the way through: the event has an album, a variant has a single hero image, so
// every action REPLACES the photo instead of appending to a list.
export { VARIANT_ID, hasVariantOverride, meetupListingUrl, eventbriteListingUrl };

// A blank field inherits from the event, so the event's own value is what the
// listing will actually use. Showing it as the placeholder says that without
// asking anyone to read a sentence first.
const inheritPlaceholder = (value, fallback) => {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return fallback;
  return text.length > 90 ? `${text.slice(0, 90)}…` : text;
};

// The photo is a URL string until the moment it is a File the save has yet to
// upload, and the preview has to keep working across that gap.
const previewSrc = (photo) => {
  if (!photo) return null;
  if (typeof photo === "string") return photo.trim() || null;
  try {
    return URL.createObjectURL(photo);
  } catch {
    return null;
  }
};

const INPUT_CLASS =
  "mt-1 px-4 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-base sm:leading-6";

const LABEL_CLASS =
  "block text-sm font-medium leading-6 text-gray-500 dark:text-gray-400";

// Quick fills for the instruction box. Every one of these changes the ANGLE —
// who it speaks to, what it leads with, how long it runs. None of them ask for
// a different night, because the second listing is the same night and anything
// that reads as "make it a drinks thing instead" is a promise nobody can keep.
const COPY_PRESETS = [
  "Shorter",
  "A different theme",
  "More casual",
  "Lead with the group plan",
  "Pitch it to newcomers",
];

const NOTE_CLASS =
  "mt-3 rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/30 p-2 text-sm text-yellow-800 dark:text-yellow-200";

/**
 * An explanation worth reading once and not on every edit, folded the same way
 * "Why a second listing" is. Only teaching folds: warnings, and anything saying
 * what the form is doing right now, stay in plain sight.
 */
const Note = ({ label, className = "", children }) => (
  <Disclosure as="div" className={className}>
    <DisclosureButton className="group flex items-center gap-1.5 rounded text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      {label}
      <ChevronDownIcon
        className="h-3.5 w-3.5 shrink-0 transition-transform group-data-[open]:rotate-180"
        aria-hidden="true"
      />
    </DisclosureButton>
    <DisclosurePanel className="mt-1 text-xs leading-relaxed text-gray-400 dark:text-gray-500">
      {children}
    </DisclosurePanel>
  </Disclosure>
);

/**
 * A plain look at the chosen image, big enough to judge as a hero.
 *
 * Deliberately NOT the event's PhotoModal. That one offers "Delete from
 * Storage", which is unsafe here: the Existing picker can hand back a URL the
 * event itself is already using, and the same object may be live on a published
 * Meetup listing. Deleting it would break both, from a screen that looks like it
 * is only editing a second listing. Clearing back to the event's photo is the
 * honest destructive action, and it lives beside the thumbnail.
 */
const VariantPhotoLightbox = ({ src, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className="relative max-w-3xl w-full"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label="Second listing photo"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-2 right-2 z-10 rounded-full bg-black bg-opacity-50 p-2 text-white hover:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <img
        src={src}
        alt="Second listing hero, enlarged"
        className="w-full h-auto max-h-[80vh] rounded-lg object-contain bg-black"
      />
    </div>
  </div>
);

const ListingVariantSection = ({
  variants,
  onChange,
  syncState,
  eventName = "",
  eventDescription = "",
  eventPhoto = "",
  eventHasVideo = false,
  onGeneratePhoto,
  generatingPhoto = false,
  onGenerateCopy,
  generatingCopy = false,
  onUploadPhoto,
  onSelectPhotoFromLibrary,
  onRecreate,
  recreating = false,
  platformsInUse,
}) => {
  const uid = useId();
  const [imageKeywords, setImageKeywords] = useState("");
  const [copyInstruction, setCopyInstruction] = useState("");
  // What the fields held before the last generation, so a take the author
  // dislikes costs one click rather than their original wording.
  const [copyUndo, setCopyUndo] = useState(null);
  const [copyWarnings, setCopyWarnings] = useState([]);
  const [showKeywords, setShowKeywords] = useState(false);
  const keywordsRef = useRef(null);

  // Close the keyword dropdown when clicking outside of it.
  useEffect(() => {
    if (!showKeywords) return;
    const handlePointerDown = (e) => {
      if (keywordsRef.current && !keywordsRef.current.contains(e.target)) {
        setShowKeywords(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showKeywords]);

  const map = variants || {};
  const variant = map[VARIANT_ID] || null;

  // Server-owned ids. They arrive from the event doc, never from form state, and
  // nothing here ever writes them back.
  const sync = syncState?.[VARIANT_ID] || {};
  const meetupUrl = meetupListingUrl(sync.meetupEventId);
  const eventbriteUrl = eventbriteListingUrl(sync.eventbriteEventId);
  const published = Boolean(meetupUrl || eventbriteUrl);

  const enabled = variant?.enabled === true;
  const filledIn = hasVariantOverride(variant);
  const photo = variant?.photo || "";
  const [enlarged, setEnlarged] = useState(false);
  // Folds like the Settings panel beside it, and starts folded for the same
  // reason: most events never touch this. Adding a listing opens it, or the
  // button would look like it did nothing.
  const [open, setOpen] = useState(false);

  // A File has no URL until one is minted for it, and minting a fresh one on
  // every render leaks a blob per keystroke elsewhere in the form. Memoized on
  // the photo itself and revoked when it is replaced.
  //
  // The revoke is feature-detected for the same reason previewSrc catches around
  // createObjectURL: this pair is missing in jsdom and patchy in older browsers,
  // and a preview that cannot be tidied up is still a preview. Never let cleanup
  // throw during an unmount.
  const preview = useMemo(() => previewSrc(photo), [photo]);
  useEffect(() => {
    if (!preview || !preview.startsWith("blob:")) return undefined;
    if (typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function") {
      return undefined;
    }
    return () => URL.revokeObjectURL(preview);
  }, [preview]);

  const patchVariant = (patch) =>
    onChange({ ...map, [VARIANT_ID]: { ...(variant || {}), ...patch } });

  const addVariant = () => {
    setOpen(true);
    onChange({
      ...map,
      [VARIANT_ID]: { enabled: true, name: "", description: "", photo: "" },
    });
  };

  const removeVariant = () => {
    const next = { ...map };
    delete next[VARIANT_ID];
    onChange(next);
  };

  // One header, one behaviour, whatever state the panel is in.
  const toggleOpen = () => {
    // Nothing here yet, so opening it is what makes the listing to edit.
    if (!variant) {
      addVariant();
      return;
    }
    // Folding one nobody typed anything into takes it back out, so the header
    // goes back to "Add a second listing". An untouched variant is not a second
    // listing, it is a form somebody opened and closed, and keeping it would
    // have the header claim a listing that has nothing in it.
    if (open && !filledIn && !published) {
      removeVariant();
      setOpen(false);
      return;
    }
    setOpen(!open);
  };

  const generate = () => {
    setShowKeywords(false);
    onGeneratePhoto?.(imageKeywords.trim());
  };

  // The event's own copy is what a second listing is written against, and the
  // schedule and venue it must not contradict are read out of it.
  const canWriteCopy = Boolean(eventName?.trim() && eventDescription?.trim());

  // Chips build an instruction rather than replace one, so "Shorter" then
  // "A different theme" reads the way someone would have typed it.
  const addPreset = (preset) =>
    setCopyInstruction((current) => {
      const text = current.trim();
      if (!text) return preset;
      if (text.toLowerCase().includes(preset.toLowerCase())) return text;
      return `${text}, ${preset.charAt(0).toLowerCase()}${preset.slice(1)}`;
    });

  const writeCopy = async () => {
    if (!canWriteCopy || generatingCopy) return;
    const before = {
      name: variant?.name || "",
      description: variant?.description || "",
    };
    setCopyWarnings([]);
    const result = await onGenerateCopy?.(copyInstruction.trim());
    // Only offer Undo for a generation that actually replaced something. On a
    // failure the fields are untouched, so there is nothing to undo.
    if (!result) return;
    setCopyUndo(before);
    setCopyWarnings(result.warnings || []);
  };

  const undoCopy = () => {
    if (!copyUndo) return;
    patchVariant(copyUndo);
    setCopyUndo(null);
    setCopyWarnings([]);
  };

  // Typing over generated copy makes the snapshot stale: restoring it would now
  // throw away the author's own edit, which is not what "Undo" should ever do.
  const editCopyField = (patch) => {
    setCopyUndo(null);
    setCopyWarnings([]);
    patchVariant(patch);
  };

  // The label is whatever the panel actually holds. A listing with nothing typed
  // into it is not a second listing yet, so it reads as the invitation to make
  // one — but a published listing keeps its name even with the fields cleared,
  // because it is live on Meetup whatever the form says.
  const hasListing = filledIn || published;

  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-stone-300 dark:border-stone-700 rounded-md">
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left focus:outline-none"
      >
        <span className="flex items-center gap-2 text-md font-medium text-gray-500 dark:text-gray-400">
          {hasListing ? (
            <MegaphoneIcon className="w-5 h-5 shrink-0" aria-hidden="true" />
          ) : (
            <PlusIcon className="w-5 h-5 shrink-0" aria-hidden="true" />
          )}
          {hasListing ? "Second listing" : "Add a second listing"}
        </span>
        <svg
          className={`w-5 h-5 transform transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && variant && (
        <div className="mt-4">
          {/* The switch cannot sit in the header: it would be a control inside a
              control. It leads the body instead, where it reads as the first
              decision about this listing. */}
          <Field className="flex shrink-0 items-center gap-2">
            <Label
              as="span"
              className="text-xs font-light text-gray-500 dark:text-gray-400"
            >
              Publish this listing
            </Label>
            <Switch
              checked={enabled}
              onChange={(checked) => patchVariant({ enabled: checked })}
              className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 dark:bg-gray-700 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary data-[checked]:bg-primary dark:data-[checked]:bg-primary"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-800 shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
              />
            </Switch>
          </Field>

          {/* The reasoning, not just the mechanics. An author who does not know WHY
              this exists writes a near-duplicate of the event, which is the one way
              to use it that makes things worse rather than better.
              Collapsed by default: it is worth reading once, not on every edit. */}
          <Disclosure as="div" className="mt-3">
            <DisclosureButton className="group flex w-full items-center gap-1.5 rounded text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              Why a second listing
              <ChevronDownIcon
                className="h-3.5 w-3.5 shrink-0 transition-transform group-data-[open]:rotate-180"
                aria-hidden="true"
              />
            </DisclosureButton>
            <DisclosurePanel className="mt-2 rounded-md bg-white dark:bg-gray-800/40 ring-1 ring-gray-200 dark:ring-gray-700 p-3">
              <ul className="space-y-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                <li>
                  Most people find these nights by scrolling Meetup, and that is our
                  biggest audience. A second listing is a second chance to catch
                  someone in that feed, with a different angle on the same night.
                </li>
                <li>
                  Both listings point at this event and the same sign-up. There is no
                  second guest list and no second page.
                </li>
                <li>
                  Write it as a genuinely different pitch. Two listings that read
                  almost the same look like a mistake and compete for the same
                  person. One leaning after-work social and one leaning speed dating
                  will reach different people. "Write it with AI" below will do that
                  for you from a short note — it keeps the date, the venue and the
                  schedule and only changes the pitch, so read it before you save.
                </li>
                <li>
                  Whoever clicks it lands on this event showing the pitch they
                  clicked, so the headline never changes under them.
                </li>
                <li>
                  Each link is tagged, so afterwards you can see which pitch actually
                  brought people in.
                </li>
              </ul>
              <p className="mt-2 text-xs leading-relaxed text-gray-400 dark:text-gray-500">
                Worth knowing: the host gets a separate text and has to publish the
                second Meetup draft by hand. Once it is live it can only be taken
                down on Meetup and Eventbrite themselves.
              </p>
            </DisclosurePanel>
          </Disclosure>

          <Note className="mt-3" label="What a blank field does">
            Leave a field blank to use the event's own name, description or photo.
            The grey text in each field shows what a blank field falls back to.
            {eventHasVideo
              ? " This event has an animated hero. Giving this listing its own photo turns the animation off for it, because the clip was made from the event's photo."
              : ""}
          </Note>

          <div className="mt-2 space-y-3">
            {/* Writing a second pitch from scratch is the reason most events never
                get one, so this sits above the fields it fills rather than beside
                them. It writes into the two fields and saves nothing: the author
                reads it, edits it, and the event doc changes only when they save. */}
            <div className="rounded-md bg-white dark:bg-gray-800/40 ring-1 ring-gray-200 dark:ring-gray-700 p-3">
              <label
                htmlFor={`${uid}-instruction`}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400"
              >
                <SparklesIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Write it with AI
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  id={`${uid}-instruction`}
                  type="text"
                  value={copyInstruction}
                  onChange={(e) => setCopyInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      writeCopy();
                    }
                  }}
                  placeholder="e.g. shorter, and pitch it at people new to the city"
                  className="px-3 block w-full min-w-0 flex-1 rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm"
                />
                <button
                  type="button"
                  onClick={writeCopy}
                  disabled={!canWriteCopy || generatingCopy}
                  className={`shrink-0 rounded border border-primary bg-white dark:bg-gray-800 px-4 py-2 text-sm text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    generatingCopy ? "animate-pulse" : "disabled:opacity-50 disabled:cursor-not-allowed"
                  }`}
                >
                  {generatingCopy ? "Writing..." : "Write it"}
                </button>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {COPY_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => addPreset(preset)}
                    className="rounded-full border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {preset}
                  </button>
                ))}
              </div>

              {canWriteCopy ? (
                <Note className="mt-2" label="What this writes">
                  Writes the name and description below from this event, following
                  your note. The date, venue and plan stay as they are. Nothing is
                  saved until you save the event.
                </Note>
              ) : (
                // Stays in plain sight: it is the reason the button is dead.
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  Give the event a name and description first — the second listing
                  is written from them.
                </p>
              )}

              {generatingCopy && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Writing a second pitch for this night. This usually takes 10-30 seconds.
                </p>
              )}

              <div aria-live="polite">
                {/* Never blocking. This is generated copy sitting in an editable
                    field that a person reads before saving, so the honest job is to
                    point at what is worth a second look. */}
                {copyWarnings.length > 0 && (
                  <ul className={`${NOTE_CLASS} list-disc space-y-1 pl-5`}>
                    {copyWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                )}
                {copyUndo && (
                  <button
                    type="button"
                    onClick={undoCopy}
                    className="mt-2 rounded text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    Undo, put back what was here
                  </button>
                )}
              </div>
            </div>

            <div>
              <label htmlFor={`${uid}-name`} className={LABEL_CLASS}>
                Listing name
              </label>
              <input
                id={`${uid}-name`}
                type="text"
                value={variant.name || ""}
                onChange={(e) => editCopyField({ name: e.target.value })}
                placeholder={inheritPlaceholder(eventName, "Same as the event name")}
                className={INPUT_CLASS}
              />
            </div>

            <div>
              <label htmlFor={`${uid}-description`} className={LABEL_CLASS}>
                Listing description
              </label>
              <textarea
                id={`${uid}-description`}
                rows={6}
                value={variant.description || ""}
                onChange={(e) => editCopyField({ description: e.target.value })}
                placeholder={inheritPlaceholder(
                  eventDescription,
                  "Same as the event description"
                )}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 shadow-sm focus:border-primaryDark focus:ring-primaryDark text-xs sm:text-sm"
              />
            </div>

            <div>
              <span className={`${LABEL_CLASS} mb-2`}>Listing photo</span>

              {/* The same three ways in the event's own photos have. Each one
                  replaces this listing's single photo rather than adding to a strip. */}
              <div className="flex gap-2 sm:gap-4">
                <div className="relative sm:max-w-40 w-1/3" ref={keywordsRef}>
                  <div className="flex">
                    <SecondaryButtonSmall
                      type="button"
                      onClick={generate}
                      loading={generatingPhoto}
                      className="flex items-center justify-center !p-2 flex-1 min-w-0 !rounded-r-none text-sm sm:text-base"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 flex-shrink-0 sm:mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="hidden sm:inline-block truncate min-w-0">
                        {generatingPhoto ? "Generating..." : "Generate"}
                      </span>
                    </SecondaryButtonSmall>
                    <button
                      type="button"
                      onClick={() => setShowKeywords((v) => !v)}
                      aria-label="Customize this listing's generated image with keywords"
                      aria-expanded={showKeywords}
                      className="relative flex flex-shrink-0 items-center justify-center px-2 bg-white dark:bg-gray-800 text-primary border border-l-0 border-primary rounded-r hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 transition-transform ${showKeywords ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                      {imageKeywords.trim() && !showKeywords && (
                        <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-primary" />
                      )}
                    </button>
                  </div>
                  {showKeywords && (
                    <div className="absolute left-0 z-20 mt-2 w-64 max-w-[80vw] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-3">
                      <label
                        htmlFor={`${uid}-keywords`}
                        className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
                      >
                        Customize image
                      </label>
                      <input
                        id={`${uid}-keywords`}
                        type="text"
                        autoFocus
                        value={imageKeywords}
                        onChange={(e) => setImageKeywords(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            generate();
                          }
                        }}
                        placeholder="e.g. rooftop sunset, string lights, cozy"
                        className="px-3 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary text-sm"
                      />
                      <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                        Keywords are added to the prompt when you tap Generate. Leave blank for an auto-generated image.
                      </p>
                    </div>
                  )}
                </div>

                <PrimaryButtonSmall
                  type="button"
                  onClick={onSelectPhotoFromLibrary}
                  className="!p-2 sm:max-w-40 w-1/3 text-sm sm:text-base"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Existing
                </PrimaryButtonSmall>

                <div className="relative flex items-center justify-center !p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer sm:max-w-40 sm:w-1/3 text-sm sm:text-base">
                  <input
                    type="file"
                    accept="image/*"
                    aria-label="Upload a photo for this listing"
                    onChange={onUploadPhoto}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mx-2 md:mr-2 "
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  <span className="hidden sm:block">Upload</span>
                </div>
              </div>

              {generatingPhoto && (
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  Generating an image from this listing's own name and description.
                  This usually takes 15-20 seconds.
                </p>
              )}

              <div className="mt-3 flex items-start gap-3">
                {generatingPhoto && <GeneratingPhotoSkeleton />}
                {!generatingPhoto && preview && (
                  <button
                    type="button"
                    onClick={() => setEnlarged(true)}
                    aria-label="View this listing's photo larger"
                    className="relative w-24 h-24 flex-shrink-0 rounded-md shadow-md overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <img
                      src={variantURL(preview, 400)}
                      alt="Second listing hero"
                      onError={fallbackToOriginal(preview, (e) => {
                        e.currentTarget.style.display = "none";
                      })}
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {photo
                      ? "Generate, Existing or Upload replaces this listing's photo."
                      : "Using the event's own photo. Generate, Existing or Upload gives this listing one of its own."}
                  </p>
                  {photo && (
                    <button
                      type="button"
                      onClick={() => patchVariant({ photo: "" })}
                      className="mt-1 rounded text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      Use the event's photo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div aria-live="polite">
            {/* Guidance, not a blocking error. The save goes through either way; what
                the author needs to understand is why nothing reached Meetup. */}
            {enabled && !filledIn && (
              <p className={NOTE_CLASS}>
                Fill in at least one of the three fields above. With all three blank
                this listing would repeat the event word for word, so it is not sent
                to Meetup or Eventbrite.
              </p>
            )}
            {/* Never imply that switching off retracts anything. It does not. */}
            {!enabled && published && (
              <p className={NOTE_CLASS}>
                This is switched off, so changes here stop reaching Meetup and
                Eventbrite. The listings below stay live on both sites until you
                delete them there yourself.
              </p>
            )}
            {!enabled && !published && (
              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                Switched off, so nothing is published. Your wording is kept.
              </p>
            )}
          </div>

          {/* Rendered whether or not anything is linked: after an unlink the ids
              vanish from the event doc straight away, and what just happened
              still has to be said. */}
          <PublishedListings
            meetupUrl={meetupUrl}
            eventbriteUrl={eventbriteUrl}
            willPublish={enabled && filledIn}
            syncScheduledFor={sync.meetupTaskScheduledFor}
            everSynced={Boolean(syncState?.[VARIANT_ID])}
            platformsInUse={platformsInUse}
            onRecreate={onRecreate}
            recreating={recreating}
          />

          {/* Removing once ids exist would orphan the live listings, so that route
              is closed. Switching off is the honest option and it says what it
              does. A listing deleted on the site itself is unlinked above, and
              once nothing is linked removal opens again. */}
          {enlarged && preview && (
            <VariantPhotoLightbox src={preview} onClose={() => setEnlarged(false)} />
          )}

          {!published && (
            <button
              type="button"
              onClick={removeVariant}
              className="mt-3 rounded text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Remove this listing
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ListingVariantSection;
